import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDate } from '../lib/dateUtils';
import { useWheelNavigation } from '../hooks/useWheelNavigation';

export const BinBoxView = ({ config, handleItemSelect }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);

  const setBinboxData = useStore((state) => state.setBinboxData);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const rawDeletedVouchers = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const vouchers = await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => !!v.deleted && v.date >= fromDate && v.date <= toDate)
      .toArray();

    const result = [];
    for (const v of vouchers) {
      const entries = await db.voucherEntries.where('voucherId').equals(v.id!).toArray();
      let particulars = 'Unknown';
      let amount = 0;

      if (entries.length > 0) {
        const drEntries = entries.filter(e => e.type === 'Dr');
        const crEntries = entries.filter(e => e.type === 'Cr');

        let partyEntry = entries[0];
        if (v.type === 'Sales' || v.type === 'Credit Note') partyEntry = drEntries[0] || entries[0];
        else if (v.type === 'Purchase' || v.type === 'Debit Note') partyEntry = crEntries[0] || entries[0];
        else if (v.type === 'Receipt') partyEntry = crEntries[0] || entries[0];
        else if (v.type === 'Payment') partyEntry = drEntries[0] || entries[0];

        const ledger = partyEntry?.ledgerId ? await db.ledgers.get(partyEntry.ledgerId) : null;
        particulars = ledger?.name || 'Unknown';

        amount = drEntries.reduce((sum, e) => sum + e.amount, 0);
      }

      result.push({
        id: v.id,
        date: v.date,
        vchType: v.type,
        vchNo: v.voucherNumber || '',
        refNo: v.referenceNumber || '',
        particulars,
        amount,
        deletedAt: v.deletedAt || '',
        deletedBy: v.deletedBy || 'System'
      });
    }

    return result.sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
  }, [activeCompany, fromDate, toDate]);

  const vouchers = useMemo(() => {
    if (!rawDeletedVouchers) return [];
    let filtered = rawDeletedVouchers;
    
    if (search.trim()) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(v => 
        v.particulars.toLowerCase().includes(lowerSearch) ||
        v.vchNo.toLowerCase().includes(lowerSearch) ||
        v.refNo.toLowerCase().includes(lowerSearch) ||
        v.amount.toString().includes(lowerSearch)
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(v => v.vchType === typeFilter);
    }

    return filtered;
  }, [rawDeletedVouchers, search, typeFilter]);

  useEffect(() => {
    setBinboxData(vouchers);
    return () => setBinboxData(null);
  }, [vouchers, setBinboxData]);

  useWheelNavigation(scrollRef, vouchers?.length || 0, setSelectedIndex);

  const processAction = (actionKey: string) => {
    if (actionKey === 'Space' || actionKey === ' ') {
      const item = vouchers[selectedIndex];
      if (item) {
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(item.id)) next.delete(item.id);
          else next.add(item.id);
          return next;
        });
      }
    } else if (actionKey === 'F2') {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'F2', altKey: true }));
    } else if (actionKey.toLowerCase() === 'q' || actionKey === 'Escape') {
      handleItemSelect({ name: 'Quit' });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'SELECT') return;

      const viewKeys = [' ', 'Enter', 'Escape', 'q', 'F2'];
      if (viewKeys.includes(e.key) || viewKeys.includes(e.key.toLowerCase())) {
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.preventDefault();
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.stopPropagation();
      }

      processAction(e.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [vouchers, selectedIndex]);

  const summary = useMemo(() => {
    if (!vouchers) return { totalCount: 0, byType: {} };
    const byType: Record<string, { count: number, totalAmount: number }> = {};
    for (const v of vouchers) {
      if (!byType[v.vchType]) byType[v.vchType] = { count: 0, totalAmount: 0 };
      byType[v.vchType].count += 1;
      byType[v.vchType].totalAmount += v.amount;
    }
    return { totalCount: vouchers.length, byType };
  }, [vouchers]);

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Bin Box (Deleted Vouchers)</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex justify-between items-center border-b border-[#aebec8] px-2 py-1 bg-gray-50 font-semibold shadow-sm">
          <div className="flex items-center gap-4">
            <span className="text-[#1e8585]">Period: {formatDate(fromDate)} to {formatDate(toDate)}</span>
            <input 
              type="text" 
              placeholder="Search party or vch no..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-2 py-1 border border-gray-300 outline-none focus:border-[#1e8585]"
            />
            <select 
              value={typeFilter} 
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-2 py-1 border border-gray-300 outline-none focus:border-[#1e8585]"
            >
              <option value="">All Types</option>
              {Object.keys(summary.byType).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
             <span className="text-gray-600">Total Entries: {summary.totalCount}</span>
          </div>
        </div>

        <div className="flex justify-between border-b border-gray-300 font-bold px-2 py-1 bg-gray-100 uppercase text-[11px] text-gray-700">
          <span className="w-[80px]">Date</span>
          <span className="w-[120px]">Vch Type</span>
          <span className="w-[80px]">Vch No.</span>
          <span className="w-[100px]">Ref No.</span>
          <span className="flex-1">Particulars / Party</span>
          <span className="w-[100px] text-right">Amount</span>
          <span className="w-[120px] text-right">Deleted At</span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto w-full scrollbar-thin">
          {vouchers.map((v: any, idx: number) => {
            const isSelectedRow = selectedIndex === idx;
            const isChecked = selectedIds.has(v.id);

            let rowClass = 'text-black hover:bg-gray-50';
            if (isChecked) {
               rowClass = isSelectedRow ? 'bg-[#1b7070] text-[#ffcc00] font-bold active-row' : 'bg-[#1e8585] text-white font-semibold';
            } else if (isSelectedRow) {
               rowClass = 'bg-[#fff9c4] text-black font-semibold active-row';
            }

            return (
              <div 
                key={v.id}
                className={`flex justify-between w-full px-2 py-[2px] cursor-pointer border-b border-gray-100 ${rowClass}`}
                onClick={() => setSelectedIndex(idx)}
              >
                <span className="w-[80px]">{formatDate(v.date)}</span>
                <span className={`w-[120px] font-semibold ${isChecked ? '' : 'text-red-600/80'}`}>{v.vchType}</span>
                <span className="w-[80px]">{v.vchNo || '-'}</span>
                <span className={`w-[100px] text-gray-600 font-semibold truncate pr-2`}>{v.refNo || '-'}</span>
                <span className={`flex-1 font-semibold truncate ${isChecked ? '' : 'text-gray-700'}`}>{v.particulars}</span>
                <span className="w-[100px] text-right">{v.amount > 0 ? v.amount.toFixed(2) : '-'}</span>
                <span className={`w-[120px] text-right ${isChecked ? 'opacity-90' : 'text-gray-500'}`}>{new Date(v.deletedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            );
          })}
          {vouchers.length === 0 && (
             <div className="text-center py-8 text-gray-400 italic">No deleted vouchers in this period.</div>
          )}
        </div>
        
        <div className="border-t border-gray-300 bg-gray-100 p-2 flex flex-wrap gap-4 text-[11.5px] text-gray-700 h-auto min-h-[30px] items-center">
           <span className="font-bold">Summary by Type:</span>
           {Object.entries(summary.byType).map(([type, data]: [string, any]) => (
              <span key={type} className="whitespace-nowrap">
                {type}: <span className="font-bold">{data.count}</span> <span className="text-gray-500 font-semibold">(₹{data.totalAmount.toFixed(2)})</span>
              </span>
           ))}
        </div>
      </div>

      {/* Shortcuts bar */}
      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto text-[11px]">
        {[
          { key: 'Q', label: 'Quit', action: () => handleItemSelect({ name: 'Quit' }) },
          { key: 'F2', label: 'Period', action: () => processAction('F2') },
          { key: 'Space', label: 'Select', action: () => processAction('Space') },
        ].map((sc, i) => (
          <div key={i} className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100"
            onClick={sc.action}>
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className="text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2">{sc.key[0]}</span>
              {sc.key.length > 1 && <span className="text-[#2a62a3] font-bold">{sc.key.substring(1)}</span>}
              <span className="text-[#2a62a3] font-bold">:</span>
              <span className="text-black leading-none">{sc.label}</span>
            </div>
            <span className="text-[#aebec8] text-[9px] mb-1">^</span>
          </div>
        ))}
      </div>
    </div>
  );
};
